/* eslint-disable */

import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronLeft, SlidersHorizontal, Sparkles, Trash2 } from 'lucide-react'
import { Input } from '../../Components/ui/input'
import { post, get, deleteReq } from '../../http/restservice'
import env from '../../env/env'
import { useModal } from '../../hooks/use-modal-store'
import { toCurrency } from '../../utils/CurrencyUtil'
import { formatIndianNumber } from '../../utils/Util'
import { cn } from '../../lib/utils'
import basketRowIcon from '../../assets/SVGS/fabits-basket-row-icon.svg'
import OrderOtpVerification from '../FirstIdeas/OrderOtpVerification'
import RedeemBreakdownSheet from './RedeemBreakdownSheet'
import {
  REDEMPTION_MODES,
  buildDynamicLines,
  extractOrderList,
  getHoldingCurrentValue,
  isRedeemOrder,
  roundRupee,
  sumLines,
  toDynamicOrderBreakdown,
  toOrderBreakdown,
} from './redeemBreakdown'

const PERCENTAGE_OPTIONS = [10, 25, 50, 75, 100]

const MODE_OPTIONS = [
  {
    id: REDEMPTION_MODES.DYNAMIC,
    label: 'Dynamic',
    badge: 'Default',
    description: 'Fabits allocates the withdrawal, and that split is sent with the order.',
    icon: Sparkles,
  },
  {
    id: REDEMPTION_MODES.CUSTOM,
    label: 'Custom',
    description: 'Starts from the redemption split. Change an amount or remove a fund.',
    icon: SlidersHorizontal,
  },
]

const formatOrderDate = (order) => {
  const raw =
    order?.createdTimestamp ||
    order?.orderPlacedTimestamp ||
    order?.updatedTimestamp ||
    order?.createdTimeStamp ||
    order?.createdAt
  if (!raw) return ''
  const normalized = String(raw).includes('T') ? String(raw) : String(raw).replace(' ', 'T')
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return String(raw)
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const getRedeemOrderAmount = (order) =>
  Number(order?.orderRedemptionAmount ?? order?.orderInvestmentAmount ?? 0)

const getRedeemOrderFunds = (order) =>
  order?.orderRedemptionBreakdown || order?.orderInvestmentBreakdown || []

const getRedeemFundAmount = (fund) =>
  Number(fund?.allocationAmount ?? fund?.withdrawableAmount ?? fund?.investmentAmount ?? 0)

const Redeem = () => {
  const user = JSON.parse(sessionStorage.getItem('actionPlanUser') || 'null')?.data
  const location = useLocation()
  const navigate = useNavigate()
  const { onOpen } = useModal()

  const basketList = useMemo(() => {
    const basket = location?.state?.basket
    if (Array.isArray(basket)) return basket
    return basket ? [basket] : []
  }, [location?.state?.basket])

  const basketInfo = basketList[0]
  const clientCode = (location?.state?.clientCode || user?.uid?.split('-')[0] || '').toUpperCase()

  const availableAmount = useMemo(
    () => basketList.reduce((sum, fund) => sum + getHoldingCurrentValue(fund), 0),
    [basketList],
  )

  const [redemptionPercentage, setRedemptionPercentage] = useState(100)
  const [redemptionAmount, setRedemptionAmount] = useState(0)
  const [amountInput, setAmountInput] = useState('0')
  const [amountError, setAmountError] = useState('')
  const [sheetOpen, setSheetOpen] = useState(false)
  const [activeMode, setActiveMode] = useState(REDEMPTION_MODES.DYNAMIC)
  const [lines, setLines] = useState([])
  const [dynamicFunds, setDynamicFunds] = useState([])
  const [staticSeed, setStaticSeed] = useState([])
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [fetchError, setFetchError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [otpSubmitting, setOtpSubmitting] = useState(false)
  const [openOTPPopup, setOpenOTPPopup] = useState(false)
  const [redemptionFund, setRedemptionFund] = useState([])
  const [redeemOrders, setRedeemOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [expandedOrderIds, setExpandedOrderIds] = useState([])
  const [deletingOrderId, setDeletingOrderId] = useState(null)

  useEffect(() => {
    const fullAmount = roundRupee(availableAmount)
    setRedemptionAmount(fullAmount)
    setAmountInput(toCurrency(fullAmount))
    setRedemptionPercentage(100)
  }, [availableAmount])

  const loadRedeemOrders = async ({ showLoading = true } = {}) => {
    if (!clientCode) {
      setRedeemOrders([])
      setOrdersLoading(false)
      return
    }

    if (showLoading) setOrdersLoading(true)
    const response = await get(
      `${env.API_URL}mutualfundservice/api/basket/subsequentOneTimeOrder`,
      null,
      { clientCode },
    )
    const basketId = basketInfo?.customerBasketInvestmentId
    const orders = extractOrderList(response)
      .filter((order) => isRedeemOrder(order))
      .filter((order) => {
        if (!basketId) return true
        return String(order?.customerBasketInvestmentId) === String(basketId)
      })
      .sort((a, b) => {
        const aTime = new Date(String(a?.createdTimestamp || '').replace(' ', 'T')).getTime()
        const bTime = new Date(String(b?.createdTimestamp || '').replace(' ', 'T')).getTime()
        return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime)
      })
    setRedeemOrders(orders)
    setOrdersLoading(false)
  }

  useEffect(() => {
    loadRedeemOrders()
  }, [clientCode, basketInfo?.customerBasketInvestmentId])

  const handleAmountChange = (event) => {
    setAmountError('')
    const raw = event.target.value.replace(/[^0-9]/g, '')
    const numeric = raw ? Number(raw) : 0
    setRedemptionAmount(numeric)
    setAmountInput(raw ? toCurrency(numeric) : '')
    setRedemptionPercentage(0)
    if (numeric > roundRupee(availableAmount)) {
      setAmountError('Entered amount exceeds available balance')
    }
  }

  const handlePercentage = (percent) => {
    setAmountError('')
    const amount = roundRupee((availableAmount * percent) / 100)
    setRedemptionPercentage(percent)
    setRedemptionAmount(amount)
    setAmountInput(toCurrency(amount))
  }

  const validateAmount = () => {
    const available = roundRupee(availableAmount)
    if (!redemptionAmount || redemptionAmount <= 0) {
      setAmountError('Enter a redemption amount')
      return false
    }
    if (redemptionAmount > available) {
      setAmountError('Entered amount exceeds available balance')
      return false
    }
    return true
  }

  const fetchRedemptionLines = async (mode) => {
    setBreakdownLoading(true)
    setFetchError('')
    const response = await post(`${env.API_URL}mutualfundservice/api/basket/redemptionFunds`, {
      customerBasketInvestmentId: basketInfo?.customerBasketInvestmentId,
      universalBasketId: basketInfo?.universalBasketId || 1,
      withdrawableAmount: roundRupee(redemptionAmount),
      isV2Enabled: true,
    })

    const funds = Array.isArray(response)
      ? response
      : Array.isArray(response?.distributionData)
      ? response.distributionData
      : response?.data
    if (response?.isError || !Array.isArray(funds) || funds.length === 0) {
      setLines([])
      setDynamicFunds([])
      setFetchError(
        response?.message || 'The redemption split could not be loaded. Please try again.',
      )
      setBreakdownLoading(false)
      return
    }

    const built = buildDynamicLines(funds).map((line) => ({ ...line, source: mode }))
    setDynamicFunds(funds)
    setStaticSeed(built)
    setLines(built)
    setBreakdownLoading(false)
  }

  const showPopup = (title, message, severity) => {
    onOpen('openAdminPopup', {
      title,
      message,
      severity,
      stayOnPage: true,
      secondaryLabel: 'User Holdings',
      secondaryPath:
        '/6fb2a4ac30ddccb8b418e27864a778dee5409a7da4c55660f49f21b8543289b4/admin/userHoldings',
    })
  }

  const buildRedemptionOrderFund = (fund) => {
    const base = {
      isin: fund.isin,
      folioNo: fund.folioNo,
      schemeCode: fund.schemeCode,
      schemeName: fund.schemeName,
      redemptionType: fund.redemptionType,
      nav: fund.nav,
    }

    if (fund.redemptionType === 'ALL_UNITS') {
      return { ...base, isAllRedeem: 'Y' }
    }

    if (fund.redemptionType === 'UNITS') {
      return {
        ...base,
        isAllRedeem: 'N',
        withdrawableUnits: fund.withdrawableUnits,
      }
    }

    return {
      ...base,
      isAllRedeem: 'N',
      withdrawableAmount: fund.withdrawableAmount,
    }
  }

  const generateOTP = async () => {
    const response = await post(
      `${env.API_URL}mutualfundservice/api/mutualFund/sendTransactionalOtp`,
      {
        phoneNumber: user?.phoneNumber,
        email: user?.email?.includes('fabits') ? '' : user?.email,
      },
    )

    if (response.isError) {
      showPopup('OTP Error', 'Failed to generate OTP. Please try again later.', 'error')
      throw new Error('Failed to generate OTP')
    }

    setOpenOTPPopup(true)
    return true
  }

  const placeBasketRedeemOrder = async (redemptionResponse) => {
    const orderData = redemptionResponse.map(buildRedemptionOrderFund)

    const orderPayload = {
      clientCode: clientCode?.toUpperCase(),
      phoneNumber: formatIndianNumber(user?.phoneNumber),
      customerBasketInvestmentId: basketInfo?.customerBasketInvestmentId,
      orderData,
    }

    const basketRedeem = await post(
      `${env.API_URL}mutualfundservice/api/basket/oneTimeRedemptionOrder`,
      orderPayload,
    )

    if (basketRedeem?.isError || basketRedeem?.status === 'FAILURE') {
      showPopup(
        'Redemption Failed!',
        basketRedeem?.message || 'There was an issue with redemption. Please try again later.',
        'error',
      )
      return null
    }

    if (basketRedeem?.status === 'SUCCESS') {
      const successOrders = basketRedeem.orders?.filter((o) => o?.status === 'SUCCESS')
      if (successOrders?.length === basketRedeem.orders?.length) {
        showPopup(
          'Redemption Initiated!',
          'Your redemption request has been successfully initiated. Redemption will be completed in 1–5 working days.',
          'success',
        )
        return basketRedeem
      }
    }

    return basketRedeem
  }

  const openBreakdown = async (mode) => {
    if (!validateAmount()) return
    setActiveMode(mode)
    setFetchError('')
    setSheetOpen(true)
    setLines([])
    await fetchRedemptionLines(mode)
  }

  const handleAmountEdit = (id, value) => {
    const numeric = Number(String(value).replace(/[^0-9]/g, '')) || 0
    setLines((current) =>
      current.map((line) => (line.id === id ? { ...line, amount: numeric } : line)),
    )
  }

  const handleFullRedeemChange = (id, checked) => {
    setLines((current) =>
      current.map((line) => {
        if (line.id !== id) return line
        if (checked) {
          return {
            ...line,
            redemptionType: 'ALL_UNITS',
            isAllRedeem: 'Y',
            amount: roundRupee(line.currentValue) || line.amount,
          }
        }
        return {
          ...line,
          redemptionType: 'VALUE',
          isAllRedeem: 'N',
        }
      }),
    )
  }

  const handleRemoveLine = (id) => {
    setLines((current) => current.filter((line) => line.id !== id))
  }

  const getSelectedBreakdownFunds = () =>
    activeMode === REDEMPTION_MODES.DYNAMIC
      ? toDynamicOrderBreakdown(dynamicFunds)
      : toOrderBreakdown(lines)

  const createRedeemOrder = async () => {
    const previewTotal = sumLines(lines)
    const orderAmount = previewTotal > 0 ? previewTotal : roundRupee(redemptionAmount)

    const payload = {
      orderType: 'REDEEM',
      clientCode,
      orderRedemptionAmount: orderAmount,
      orderRedemptionBreakdown: getSelectedBreakdownFunds(),
      customerBasketInvestmentId: basketInfo?.customerBasketInvestmentId,
    }

    setSubmitting(true)
    const response = await post(
      `${env.API_URL}mutualfundservice/api/basket/subsequentOneTimeOrder`,
      payload,
    )

    setSubmitting(false)

    const successMessage = 'Subsequent one time order record created successfully'
    const responseMessage =
      typeof response === 'string' ? response : response?.message || response?.data || ''
    const succeeded =
      !response?.isError &&
      (responseMessage === successMessage ||
        String(responseMessage).includes(successMessage) ||
        response?.status === 'SUCCESS')

    if (!succeeded) {
      showPopup(
        'Redemption not created',
        (typeof response === 'string' ? response : response?.message) ||
          'The redemption order could not be created. Please try again.',
        'error',
      )
      return
    }

    setSheetOpen(false)
    showPopup(
      'Redemption created',
      typeof responseMessage === 'string' && responseMessage
        ? responseMessage
        : 'The subsequent redemption order is ready. It will show in the redemption section.',
      'success',
    )
    loadRedeemOrders()
  }

  const deleteRedeemOrder = async (order) => {
    if (!order?.id || deletingOrderId) return

    setDeletingOrderId(order.id)
    try {
      const url = `${env.API_URL}adminservice/api/actionPlan/removeSubsequentOneTimeOrder/${order.id}`
      const response = await deleteReq(url)

      if (response?.status === 200) {
        await loadRedeemOrders({ showLoading: false })
        showPopup('Redemption deleted', 'The redemption order was removed.', 'success')
        return
      }

      showPopup(
        'Could not delete',
        response?.message || 'The redemption order could not be removed. Please try again.',
        'error',
      )
    } catch {
      showPopup(
        'Could not delete',
        'The redemption order could not be removed. Please try again.',
        'error',
      )
    } finally {
      setDeletingOrderId(null)
    }
  }

  const completeWithOtp = async () => {
    const funds = getSelectedBreakdownFunds()
    if (!funds?.length) {
      showPopup(
        'Redemption Failed!',
        'No funds available in this redemption split. Please try again.',
        'error',
      )
      return
    }

    try {
      setOtpSubmitting(true)
      setRedemptionFund(funds)
      await generateOTP()
      setSheetOpen(false)
    } catch (err) {
      console.error('OTP redemption error:', err)
      showPopup('OTP Error', 'Failed to start OTP verification. Please try again later.', 'error')
    } finally {
      setOtpSubmitting(false)
    }
  }

  const handleBreakdownOpenChange = (open) => {
    if (!open && (submitting || otpSubmitting)) return
    setSheetOpen(open)
  }

  if (!basketList.length) {
    return (
      <div className='mx-auto w-full max-w-3xl px-4 py-8'>
        <button
          type='button'
          className='mb-6 inline-flex items-center gap-1 text-sm font-semibold text-[var(--fb-text)]'
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className='h-4 w-4' />
          Back to holdings
        </button>
        <div className='rounded-3xl border border-dashed border-[var(--fb-border)] bg-[var(--fb-surface)] px-6 py-12 text-center'>
          <p className='font-DMSans text-lg font-semibold text-[var(--fb-text-strong)]'>
            Open a basket to redeem
          </p>
          <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
            Choose Redeem from the client’s basket holdings.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='mx-auto w-full px-4 py-4 lg:w-[80%] lg:px-0 lg:py-6'>
        <button
          type='button'
          className='mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--fb-text-secondary)]'
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className='h-4 w-4' />
          Holdings
        </button>

        <div className='mb-5'>
          <p className='text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
            Basket redemption
          </p>
          <h1 className='font-DMSans text-2xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-3xl'>
            {basketInfo?.customerBasketName || 'Basket'}
          </h1>
        </div>

        <div className='grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] lg:gap-5'>
          <section className='rounded-3xl border border-[var(--fb-border)] bg-[var(--fb-surface)] p-5 shadow-[var(--fb-shadow-sm)] md:p-6'>
            <div className='flex items-center gap-3 rounded-2xl bg-[var(--fb-surface-muted)] px-4 py-4'>
              <img src={basketRowIcon} alt='' className='h-12 w-12' />
              <div>
                <p className='text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
                  Available balance
                </p>
                <p className='font-DMSans text-2xl font-bold tracking-tight text-[var(--fb-text-strong)]'>
                  ₹{toCurrency(Math.floor(availableAmount))}
                </p>
              </div>
            </div>

            <div className='mt-6'>
              <label htmlFor='redeem-amount' className='text-sm font-medium text-[var(--fb-text)]'>
                Withdrawal amount
              </label>
              <Input
                id='redeem-amount'
                startAdornment='₹'
                inputMode='numeric'
                value={amountInput}
                onChange={handleAmountChange}
                className='mt-2 h-12 rounded-xl border-[var(--fb-border)] bg-[var(--fb-surface-muted)] text-lg font-semibold'
              />
              {amountError ? (
                <p className='mt-2 text-sm font-medium text-[var(--fb-danger)]'>{amountError}</p>
              ) : (
                <p className='mt-2 text-xs text-[var(--fb-text-muted)]'>
                  Choose a redemption type to review how this amount is split.
                </p>
              )}
              <div className='mt-3 flex flex-wrap gap-2'>
                {PERCENTAGE_OPTIONS.map((percent) => {
                  const active = percent === redemptionPercentage
                  return (
                    <button
                      key={percent}
                      type='button'
                      onClick={() => handlePercentage(percent)}
                      className={cn(
                        'h-9 rounded-full border px-4 text-sm font-semibold transition',
                        active
                          ? 'border-[var(--fb-accent)] bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'
                          : 'border-[var(--fb-border)] bg-[var(--fb-surface)] text-[var(--fb-text)] hover:bg-[var(--fb-surface-muted)]',
                      )}
                    >
                      {percent === 100 ? 'Full' : `${percent}%`}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className='mt-6'>
              <p className='text-sm font-medium text-[var(--fb-text)]'>Redemption type</p>
              <div className='mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2'>
              {MODE_OPTIONS.map((mode) => {
                const Icon = mode.icon
                return (
                  <button
                    key={mode.id}
                    type='button'
                    onClick={() => openBreakdown(mode.id)}
                    className='flex flex-col items-start gap-3 rounded-2xl border border-[var(--fb-border)] bg-[var(--fb-surface)] p-4 text-left transition hover:border-[var(--fb-accent)] hover:bg-[var(--fb-accent-soft)]'
                  >
                    <span className='flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--fb-accent-soft)] text-[var(--fb-accent)]'>
                      <Icon className='h-5 w-5' />
                    </span>
                      <span>
                        <span className='flex items-center gap-2 font-semibold text-[var(--fb-text-strong)]'>
                          {mode.label}
                          {mode.badge ? (
                            <span className='rounded-full bg-[var(--fb-accent)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white'>
                              {mode.badge}
                            </span>
                          ) : null}
                        </span>
                        <span className='mt-1 block text-xs leading-5 text-[var(--fb-text-muted)]'>
                          {mode.description}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </section>

          <section className='rounded-3xl border border-[var(--fb-border)] bg-[var(--fb-surface)] p-5 shadow-[var(--fb-shadow-sm)] md:p-6'>
            <h2 className='font-DMSans text-lg font-bold text-[var(--fb-text-strong)]'>
              Redemption orders
            </h2>
            <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
              Subsequent orders with type Redeem for this basket.
            </p>

            <div className='mt-4 space-y-3'>
              {ordersLoading ? (
                <div className='space-y-3'>
                  {[0, 1].map((item) => (
                    <div
                      key={item}
                      className='h-20 animate-pulse rounded-2xl bg-[var(--fb-surface-muted)]'
                    />
                  ))}
                </div>
              ) : null}

              {!ordersLoading && redeemOrders.length === 0 ? (
                <div className='rounded-2xl border border-dashed border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-4 py-8 text-center'>
                  <p className='font-semibold text-[var(--fb-text-strong)]'>No redemption orders</p>
                  <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
                    Create one from Dynamic or Custom.
                  </p>
                </div>
              ) : null}

              {!ordersLoading &&
                redeemOrders.map((order, index) => {
                  const orderKey = String(
                    order?.id || `${order?.customerBasketInvestmentId}-${index}`,
                  )
                  const funds = getRedeemOrderFunds(order)
                  const status = order?.orderStatus || 'PENDING'
                  const isExpanded = expandedOrderIds.includes(orderKey)

                  return (
                    <article
                      key={orderKey}
                      className='rounded-2xl border border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-4 py-3'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <button
                          type='button'
                          className='min-w-0 flex-1 text-left'
                          aria-expanded={isExpanded}
                          onClick={() =>
                            setExpandedOrderIds((current) =>
                              current.includes(orderKey)
                                ? current.filter((id) => id !== orderKey)
                                : [...current, orderKey],
                            )
                          }
                        >
                          <p className='font-DMSans text-lg font-bold text-[var(--fb-text-strong)]'>
                            ₹{toCurrency(getRedeemOrderAmount(order))}
                          </p>
                          <p className='text-xs text-[var(--fb-text-muted)]'>
                            {funds.length} {funds.length === 1 ? 'fund' : 'funds'}
                            {formatOrderDate(order) ? ` · ${formatOrderDate(order)}` : ''}
                          </p>
                        </button>
                        <div className='flex shrink-0 items-center gap-2'>
                          <span className='rounded-full bg-[var(--fb-surface)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--fb-accent-text)]'>
                            {status}
                          </span>
                          {order?.id ? (
                            <button
                              type='button'
                              aria-label='Delete redemption order'
                              disabled={deletingOrderId === order.id}
                              className='inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--fb-text-muted)] hover:bg-[var(--fb-danger-bg)] hover:text-[var(--fb-danger)] disabled:opacity-50'
                              onClick={() => deleteRedeemOrder(order)}
                            >
                              <Trash2 className='h-4 w-4' />
                            </button>
                          ) : null}
                          <button
                            type='button'
                            aria-label={isExpanded ? 'Hide funds' : 'Show funds'}
                            className='inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--fb-text-muted)] hover:bg-[var(--fb-surface)]'
                            onClick={() =>
                              setExpandedOrderIds((current) =>
                                current.includes(orderKey)
                                  ? current.filter((id) => id !== orderKey)
                                  : [...current, orderKey],
                              )
                            }
                          >
                            <ChevronDown
                              className={cn(
                                'h-4 w-4 transition-transform',
                                isExpanded && 'rotate-180',
                              )}
                            />
                          </button>
                        </div>
                      </div>
                      {isExpanded && funds.length > 0 ? (
                        <ul className='mt-3 space-y-2 border-t border-[var(--fb-border)] pt-3'>
                          {funds.map((fund, fundIndex) => (
                            <li
                              key={`${fund?.isin || fund?.schemeCode || 'fund'}-${fundIndex}`}
                              className='flex items-start justify-between gap-3 text-sm'
                            >
                              <span className='min-w-0 text-[var(--fb-text)]'>
                                <span className='block truncate font-medium'>
                                  {fund?.schemeName || fund?.legalName || 'Fund'}
                                </span>
                                {fund?.folioNo ? (
                                  <span className='block truncate text-xs text-[var(--fb-text-muted)]'>
                                    Folio {fund.folioNo}
                                  </span>
                                ) : null}
                              </span>
                              <span className='shrink-0 font-semibold text-[var(--fb-text-strong)]'>
                                ₹{toCurrency(getRedeemFundAmount(fund))}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </article>
                  )
                })}
            </div>
          </section>
        </div>

        <RedeemBreakdownSheet
          open={sheetOpen}
          mode={activeMode}
          lines={lines}
          loading={breakdownLoading}
          fetchError={fetchError}
          availableAmount={availableAmount}
          submitting={submitting}
          otpSubmitting={otpSubmitting}
          onOpenChange={handleBreakdownOpenChange}
          onAmountChange={handleAmountEdit}
          onFullRedeemChange={handleFullRedeemChange}
          onRemove={handleRemoveLine}
          onRestore={() => setLines(staticSeed)}
          onRetry={() => fetchRedemptionLines(activeMode)}
          onConfirm={createRedeemOrder}
          onCompleteWithOtp={completeWithOtp}
        />
      </div>

      <OrderOtpVerification
        phoneNumber={user?.phoneNumber}
        openOTPPopup={openOTPPopup}
        setOpenOTPPopup={setOpenOTPPopup}
        email={user?.email}
        handleSipOrOT={() => placeBasketRedeemOrder(redemptionFund)}
      />
    </>
  )
}

export default Redeem
